import {
    Alert,
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Link,
    TextField,
    Typography,
} from '@mui/material';
import { JSX, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { termsAndConditionsLink } from '../../../../library/constants';
import { fieldProps, useValidatedForm } from '../../../../library/formValidation';
import {
    IBookingRequestAccepted,
    IBookingRequestSubmission,
    PUBLIC_BOOKING_CONSENT_VERSION,
} from '../../../../library/models/publicBooking';
import { useApiFormError } from '../../../../library/useApiFormError';
import ResourcesIds from '../../../../resources/ResourcesIds';
import { useReactMutation } from '../../../../services/queryService';
import { services } from '../../../../services/services';
import { createSignupSchema, PHONE_MAX, SignupValues } from '../publicBookingSchema';
import {
    agreementBoxStyles,
    agreementParagraphStyles,
    agreementTitleStyles,
    consentStyles,
    fieldStyles,
    formAlertStyles,
    honeypotStyles,
    submitRowStyles,
} from './signupForm.styles';

/** What was chosen on the steps above, which this form sends along with who is asking. */
export interface BookingChoice {
    serviceId: number;
    therapistId: number;
    startsAt: string;
    note?: string;
}

interface SignupFormProps {
    booking: BookingChoice;
    onSent: (submission: IBookingRequestSubmission) => void;
}

/**
 * A new visitor's details, sent for confirmation by email.
 *
 * **No password field.** The password is chosen on the page the emailed link opens, so the only
 * person who can ever set one is whoever reads that inbox. Collected here, it would let anybody
 * pre-register somebody else's address with a password of their own choosing.
 *
 * The data-processing agreement is shown **in full**, word for word the intake's
 * (`evaluation_gdpr_*`), because the consent given here is the one the clinic keeps on file:
 * the patient's first evaluation will not ask again.
 */
export const SignupForm = ({ booking, onSent }: SignupFormProps): JSX.Element => {
    const { t: translate, i18n } = useTranslation();

    const schema = useMemo(() => createSignupSchema(translate), [translate]);

    const { register, handleSubmit, setError, watch, formState: { errors, isValid } } =
        useValidatedForm<SignupValues>(schema, {
            defaultValues: {
                emailAddress: '',
                firstName: '',
                lastName: '',
                phoneNumber: '',
                gdprConsent: false,
                termsAccepted: false,
                website: '',
            },
        });

    const apiError = useApiFormError(setError, Object.keys(schema.fields), translate,
        ResourcesIds.PUBLIC_BOOKING_SUBMIT_FAILED);

    const submit = useReactMutation<IBookingRequestAccepted, Error, IBookingRequestSubmission>(
        services.publicBooking.submitRequestMutation()
    );

    const onValid = (values: SignupValues): void => {
        const submission: IBookingRequestSubmission = {
            ...values,
            emailAddress: values.emailAddress.trim(),
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
            phoneNumber: values.phoneNumber.trim(),
            ...booking,
            consentVersion: PUBLIC_BOOKING_CONSENT_VERSION,
            // The language they are reading in, which a new account then starts in.
            language: i18n.resolvedLanguage === 'en' ? 'en' : 'ro',
        };

        apiError.clear();
        submit.mutate(submission, {
            onSuccess: () => onSent(submission),
            onError: apiError.report,
        });
    };

    return (
        <Box component="form" noValidate onSubmit={handleSubmit(onValid)}>
            {apiError.formError && (
                <Alert severity="error" sx={formAlertStyles}>{apiError.formError}</Alert>
            )}

            <TextField
                fullWidth
                type="email"
                autoComplete="email"
                sx={fieldStyles}
                label={translate(ResourcesIds.EMAIL_LABEL)}
                {...register('emailAddress')}
                {...fieldProps(errors, 'emailAddress')}
            />
            <TextField
                fullWidth
                autoComplete="given-name"
                sx={fieldStyles}
                label={translate(ResourcesIds.FIRST_NAME_LABEL)}
                {...register('firstName')}
                {...fieldProps(errors, 'firstName')}
            />
            <TextField
                fullWidth
                autoComplete="family-name"
                sx={fieldStyles}
                label={translate(ResourcesIds.LAST_NAME_LABEL)}
                {...register('lastName')}
                {...fieldProps(errors, 'lastName')}
            />
            <TextField
                fullWidth
                type="tel"
                autoComplete="tel"
                sx={fieldStyles}
                label={translate(ResourcesIds.PHONE_LABEL)}
                slotProps={{ htmlInput: { maxLength: PHONE_MAX } }}
                {...register('phoneNumber')}
                {...fieldProps(errors, 'phoneNumber')}
            />

            <Box sx={honeypotStyles} aria-hidden="true">
                <TextField
                    label={translate(ResourcesIds.PUBLIC_BOOKING_HONEYPOT_LABEL)}
                    autoComplete="off"
                    slotProps={{ htmlInput: { tabIndex: -1 } }}
                    {...register('website')}
                />
            </Box>

            <Box sx={agreementBoxStyles}>
                <Typography sx={agreementTitleStyles}>
                    {translate(ResourcesIds.EVALUATION_GDPR_TITLE)}
                </Typography>
                <Typography sx={agreementParagraphStyles}>
                    {translate(ResourcesIds.EVALUATION_GDPR_PARAGRAPH_1)}
                </Typography>
                <Typography sx={agreementParagraphStyles}>
                    {translate(ResourcesIds.EVALUATION_GDPR_PARAGRAPH_2)}
                </Typography>
                <Typography sx={agreementParagraphStyles}>
                    {translate(ResourcesIds.EVALUATION_GDPR_PARAGRAPH_3)}
                </Typography>
            </Box>

            <FormControl error={!!errors.gdprConsent} sx={consentStyles}>
                <FormControlLabel
                    control={<Checkbox checked={watch('gdprConsent')} {...register('gdprConsent')} />}
                    label={translate(ResourcesIds.PUBLIC_BOOKING_GDPR_CONSENT_LABEL)}
                />
                {errors.gdprConsent && <FormHelperText>{errors.gdprConsent.message}</FormHelperText>}
            </FormControl>

            <FormControl error={!!errors.termsAccepted} sx={consentStyles}>
                <FormControlLabel
                    control={<Checkbox checked={watch('termsAccepted')} {...register('termsAccepted')} />}
                    label={
                        <>
                            {translate(ResourcesIds.TERMS_AND_CONDITIONS_PREFIX)}{' '}
                            <Link href={termsAndConditionsLink} target="_blank" rel="noopener noreferrer">
                                {translate(ResourcesIds.TERMS_AND_CONDITIONS)}
                            </Link>
                        </>
                    }
                />
                {errors.termsAccepted && <FormHelperText>{errors.termsAccepted.message}</FormHelperText>}
            </FormControl>

            <Box sx={submitRowStyles}>
                <Button type="submit" variant="contained" disabled={!isValid || submit.isPending}>
                    {translate(ResourcesIds.PUBLIC_BOOKING_SUBMIT)}
                </Button>
                {!isValid && (
                    <FormHelperText>{translate(ResourcesIds.FORM_BLOCKED_SAVE)}</FormHelperText>
                )}
            </Box>
        </Box>
    );
};
